/**
 * The root poller's client over `AuditChangesRpc` (spec 2026-09-25-001 §5.3
 * and §5.5), shaped like the ledger's `audit-ledger-rpc-client.ts`: the
 * contract comes through the leaf's `presentation/` subpath, never its root
 * barrel, which has a `server.ts` beside it (packages-and-boundaries.md).
 *
 * PINNED TO `insurance-broker`. The root poller runs only while the scope
 * store reads `insurance-broker`, and every page it gets is applied to the broker's QueryClient. The
 * store-reading `AppHttpClient.layer` could stamp a poll with another
 * product's header in the window where a section provider has written the
 * store and the poller has not yet paused, and the answer would then be that
 * product's firm, with its cursors, applied to the broker's cache.
 * `layerForScope("insurance-broker")` makes the header and the cache agree by
 * construction.
 */
import { Effect, Layer } from "effect"
import { RpcClient, RpcSerialization } from "@effect/rpc"

import type { ChangesCursor, ChangesPage } from "@/contexts/audit/changes/domain/changes-page"
import { AuditChangesRpc } from "@/contexts/audit/changes/presentation/audit-changes-rpc"
import { AppHttpClient } from "@/shared/scope"

export const AuditChangesRpcLive = RpcClient.layerProtocolHttp({
  url: "/api/rpc/audit/changes",
}).pipe(
  Layer.provide(AppHttpClient.layerForScope("insurance-broker")),
  Layer.provideMerge(RpcSerialization.layerJson),
)

/**
 * One poll. `cursor` is omitted on a fresh tab, which gets both tails and no
 * tables. `xids` names the tab's own recent writes (query speed Q11), so the
 * page marks the tables only those writes touched as `echoed`; none are sent
 * when there are none. Rejects on any failure, the contract's wire errors
 * included; the poller owns the retry. `signal` interrupts the request when
 * the poller stops.
 */
export const fetchChangesPage = (
  cursor: ChangesCursor | undefined,
  xids: ReadonlyArray<string>,
  signal?: AbortSignal,
): Promise<ChangesPage> =>
  Effect.runPromise(
    Effect.gen(function* () {
      const client = yield* RpcClient.make(AuditChangesRpc)
      return yield* client.changesSince({
        ...(cursor === undefined ? {} : { cursor }),
        ...(xids.length === 0 ? {} : { xids }),
      })
    }).pipe(
      Effect.scoped,
      Effect.withSpan("AuditChangesRpc.changesSince"),
      // A poll that has not answered in 30 s is not going to; fail it so the
      // loop backs off instead of waiting on it forever.
      Effect.timeout("30 seconds"),
      Effect.provide(AuditChangesRpcLive),
    ),
    { signal },
  )
